import { moscowDate } from '../domain/time'
import { useState } from 'react'
import { command, useProject } from '../app/data'
import { ActionForm, dateTime, localDateTime, toISO, number, type Values } from './forms'
import { ObjectRows, Tabs } from './controls'
import { Badge, Modal } from './ui'
import { Attachments } from './Attachments'
import { workStatus, eventLabels } from '../domain/selectors'
import type { WorkOrder } from '../domain/operations'

export function MaterialsEditor({
  value,
  onChange,
  includeFuel = false,
}: {
  value: unknown
  onChange: (value: Values[]) => void
  includeFuel?: boolean
}) {
  const data = useProject()
  return (
    <ObjectRows
      title="Материалы и потребность"
      rows={(value ?? []) as Values[]}
      onChange={onChange}
      empty={{ stockId: '', quantity: 1 }}
      fields={[
        {
          name: 'stockId',
          label: 'Номенклатура',
          type: 'select',
          options: [
            ...(includeFuel
              ? data.fuelStock.map((f) => ({ value: f.id, label: f.name + ' · ' + number(f.balance) + ' л' }))
              : []),
            ...data.consumables.map((m) => ({
              value: m.id,
              label: m.name + ' · ' + number(m.available) + ' ' + m.unit,
            })),
          ],
        },
        { name: 'quantity', label: 'Количество', type: 'number', min: 0.001 },
      ]}
    />
  )
}
export function WorkOrderForm({
  work,
  equipmentId,
  kind = 'repair',
  onDone,
}: {
  work?: WorkOrder
  equipmentId?: string
  kind?: 'repair' | 'maintenance'
  onDone: () => void
}) {
  const d = useProject()
  const day = moscowDate()
  return (
    <ActionForm
      initial={
        work
          ? { ...work, startAt: localDateTime(work.startAt), endAt: localDateTime(work.endAt) }
          : {
              title: '',
              kind,
              equipmentId: equipmentId ?? d.equipment[0]?.id,
              startAt: day + 'T09:00',
              endAt: day + 'T12:00',
              pipelineId: d.pipelines.find((p) => p.kind === kind)?.id,
              materials: [],
              assigneeIds: [],
            }
      }
      fields={(v) => [
        { name: 'title', label: 'Название работы', wide: true },
        {
          name: 'kind',
          label: 'Тип работы',
          type: 'select',
          disabled: !!work,
          options: [
            { value: 'repair', label: 'Ремонт' },
            { value: 'maintenance', label: 'Техническое обслуживание' },
          ],
        },
        {
          name: 'equipmentId',
          label: 'Техника',
          type: 'select',
          options: d.equipment.map((e) => ({ value: e.id, label: e.plate + ' · ' + e.model })),
        },
        { name: 'startAt', label: 'Начало, МСК', type: 'datetime-local' },
        { name: 'endAt', label: 'Окончание, МСК', type: 'datetime-local' },
        {
          name: 'pipelineId',
          label: 'Процесс выполнения',
          type: 'select',
          disabled: !!work,
          options: d.pipelines.filter((p) => p.kind === v.kind).map((p) => ({ value: p.id, label: p.name })),
        },
        {
          name: 'requestId',
          label: 'Связанная заявка',
          type: 'select',
          required: false,
          options: d.requests
            .filter((r) => r.status !== 'completed' && r.status !== 'cancelled')
            .map((r) => ({ value: r.id, label: r.number + ' · ' + r.client })),
        },
        {
          name: 'contractorId',
          label: 'Подрядчик',
          type: 'select',
          required: false,
          options: d.contractors.map((c) => ({ value: c.id, label: c.name })),
        },
        { name: 'comment', label: 'Описание и условия', type: 'textarea', required: false, wide: true },
      ]}
      submit={(v, key) =>
        command('work.save', work?.id ?? '', { ...v, startAt: toISO(v.startAt), endAt: toISO(v.endAt) }, key)
      }
      onDone={onDone}
    >
      {(v, set) => (
        <>
          <h3>Исполнители</h3>
          <div className="selection-grid">
            {d.workers.map((worker) => (
              <label className="check-option" key={worker.id}>
                <input
                  type="checkbox"
                  checked={((v.assigneeIds ?? []) as string[]).includes(worker.id)}
                  onChange={(e) =>
                    set(
                      'assigneeIds',
                      e.target.checked
                        ? [...((v.assigneeIds ?? []) as string[]), worker.id]
                        : ((v.assigneeIds ?? []) as string[]).filter((id) => id !== worker.id),
                    )
                  }
                />
                {worker.name}
                <small>{worker.role}</small>
              </label>
            ))}
          </div>
          <MaterialsEditor value={v.materials} onChange={(value) => set('materials', value)} />
        </>
      )}
    </ActionForm>
  )
}
export function WorkOrderDetail({
  id,
  onClose,
  mechanic = false,
}: {
  id: string
  onClose: () => void
  mechanic?: boolean
}) {
  const d = useProject()
  const [editing, setEditing] = useState(false)
  const [tab, setTab] = useState('overview')
  const [cancel, setCancel] = useState(false)
  const w = d.workOrders.find((w) => w.id === id)
  if (!w) return null
  const e = d.equipment.find((e) => e.id === w.equipmentId)
  const index = w.stages.findIndex((s) => s.id === w.stageId)
  const next = w.stages[index + 1]
  const closed = w.status === 'completed' || w.status === 'cancelled'
  return (
    <Modal title={w.number + ' · ' + w.title} onClose={onClose}>
      {editing ? (
        <WorkOrderForm work={w} onDone={() => setEditing(false)} />
      ) : (
        <>
          <div className="detail-actions">
            <Badge tone={closed ? 'green' : 'blue'}>{workStatus[w.status]}</Badge>
            {w.demo && <Badge>Тестовое событие</Badge>}
            {!mechanic && !closed && (
              <button className="button secondary" onClick={() => setEditing(true)}>
                Редактировать
              </button>
            )}
          </div>
          <p>
            <strong>
              {e?.plate} · {e?.model}
            </strong>
          </p>
          <p>
            {dateTime(w.startAt)} — {dateTime(w.endAt)}
          </p>
          <div className="pipeline-track">
            {w.stages.map((stage, i) => (
              <div key={stage.id} className={i <= index ? 'reached' : ''}>
                <span>{i + 1}</span>
                <strong>{stage.name}</strong>
              </div>
            ))}
          </div>
          <Tabs
            value={tab}
            onChange={setTab}
            items={[
              { value: 'overview', label: 'Работа' },
              { value: 'materials', label: 'Материалы', count: w.materials.length },
              { value: 'documents', label: 'Документы' },
              { value: 'history', label: 'История' },
            ]}
          />
          {tab === 'overview' && (
            <>
              <dl className="detail-grid">
                <div>
                  <dt>Исполнители</dt>
                  <dd>
                    {w.assigneeIds
                      .map((id) => d.workers.find((worker) => worker.id === id)?.name)
                      .join(', ') || 'Не назначены'}
                  </dd>
                </div>
                <div>
                  <dt>Подрядчик</dt>
                  <dd>{d.contractors.find((c) => c.id === w.contractorId)?.name || 'Собственные силы'}</dd>
                </div>
                <div>
                  <dt>Заявка</dt>
                  <dd>{d.requests.find((r) => r.id === w.requestId)?.number || 'Внутренняя работа'}</dd>
                </div>
                <div>
                  <dt>Описание</dt>
                  <dd>{w.comment || '—'}</dd>
                </div>
              </dl>
              {!closed && (
                <section className="stage-action">
                  <h3>{cancel ? 'Отмена работы' : 'Следующий этап: ' + next?.name}</h3>
                  <ActionForm
                    key={w.stageId + String(cancel)}
                    initial={{}}
                    fields={[
                      {
                        name: 'comment',
                        label: 'Результат этапа / комментарий',
                        type: 'textarea',
                        wide: true,
                      },
                    ]}
                    submit={(v, key) =>
                      command('work.transition', w.id, { ...v, stageId: next?.id, cancel }, key)
                    }
                    label={
                      cancel
                        ? 'Подтвердить отмену'
                        : index === w.stages.length - 2
                          ? 'Завершить и списать материалы'
                          : 'Перейти на следующий этап'
                    }
                    onDone={() => setCancel(false)}
                  />
                  <button className="button ghost" onClick={() => setCancel(!cancel)}>
                    {cancel ? 'Продолжить работу' : 'Отменить заказ-наряд'}
                  </button>
                </section>
              )}
            </>
          )}
          {tab === 'materials' && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Материал</th>
                    <th>Потребность</th>
                    <th>На складе</th>
                    <th>Обеспечение</th>
                  </tr>
                </thead>
                <tbody>
                  {w.materials.map((line) => {
                    const item = d.consumables.find((m) => m.id === line.stockId)
                    return (
                      <tr key={line.stockId}>
                        <td>{item?.name}</td>
                        <td>
                          {number(line.quantity)} {item?.unit}
                        </td>
                        <td>{number(item?.available ?? 0)}</td>
                        <td>
                          <Badge tone={(item?.available ?? 0) < line.quantity ? 'red' : 'green'}>
                            {(item?.available ?? 0) < line.quantity ? 'Дефицит' : 'В наличии'}
                          </Badge>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          {tab === 'documents' && <Attachments binding={{ workOrderId: w.id }} readOnly={mechanic} />}
          {tab === 'history' && (
            <div className="event-timeline">
              {d.events
                .filter((event) => event.entityId === id)
                .map((event) => (
                  <article key={event.id}>
                    <span>{dateTime(event.createdAt)}</span>
                    <strong>{eventLabels[event.action] ?? event.action}</strong>
                    <small>{event.actor}</small>
                    {event.detail && <p>{event.detail}</p>}
                  </article>
                ))}
            </div>
          )}
        </>
      )}
    </Modal>
  )
}
